import { useState } from 'react';
import { Copy, Search } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { Button } from '@/shared/components/shadcn/button';
import { Badge } from '@/shared/components/shadcn/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/shadcn/sheet';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { pendingAutoSearchAtom, searchRequestAtom } from '@/features/discovery/stores/discovery-store';
import type { InsightCard, CreatorGuidePrompt } from '@/features/campaign-analysis/types';

interface BrandInfo {
  brandName: string;
  productName: string;
  description: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insights: InsightCard[];
  brandInfo: BrandInfo | null;
  creatorGuidePrompts: CreatorGuidePrompt[];
}

export function PlanningBriefSheet({ open, onOpenChange, insights, brandInfo, creatorGuidePrompts }: Props) {
  const { workspace } = useWorkspace();
  const isKo = workspace.language === 'ko';
  const navigate = useNavigate();
  const { workspaceId } = useWorkspaceId();

  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const setSearchRequest = useSetAtom(searchRequestAtom);
  const setPendingAutoSearch = useSetAtom(pendingAutoSearchAtom);

  const doInsights = insights.filter((i) => i.type === 'do');
  const dontInsights = insights.filter((i) => i.type === 'dont');

  const getContentGuide = (insight: InsightCard) =>
    isKo
      ? (insight.contentGuide ?? insight.detail)
      : (insight.contentGuideEn ?? insight.contentGuide ?? insight.detailEn ?? insight.detail);

  const handleCopyPrompt = (idx: number, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleFindCreators = (prompt: string) => {
    setSearchRequest((prev) => ({ ...prev, message: prompt }));
    setPendingAutoSearch(true);
    onOpenChange(false);
    navigate({ to: '/workspace/$workspaceId/discovery/', params: { workspaceId } });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden sm:max-w-xl">
        <SheetHeader className="shrink-0 border-b pb-4">
          <SheetTitle className="text-base">Content Brief</SheetTitle>
          {brandInfo && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              <Badge variant="secondary" className="text-xs font-normal">{brandInfo.brandName}</Badge>
              <Badge variant="secondary" className="text-xs font-normal">{brandInfo.productName}</Badge>
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {/* ── Section 1: Content Guide ── */}
          {doInsights.length > 0 && (
            <section className="border-b p-5">
              <h3 className="mb-4 text-sm font-semibold text-foreground">
                {isKo ? 'Content Guide' : 'Content Guide'}
              </h3>
              <div className="flex flex-col gap-5">
                {doInsights.map((insight, i) => (
                  <div key={i} className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <Badge className="h-auto bg-emerald-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                        DO
                      </Badge>
                      <p className="text-xs font-semibold leading-snug text-foreground">
                        {isKo ? insight.title : (insight.titleEn ?? insight.title)}
                      </p>
                    </div>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {getContentGuide(insight)}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* What to Avoid */}
          {dontInsights.length > 0 && (
            <section className="border-b p-5">
              <h3 className="mb-3 text-sm font-semibold text-rose-600">
                {isKo ? '피해야 할 것' : 'What to Avoid'}
              </h3>
              <ul className="flex flex-col gap-3">
                {dontInsights.map((insight, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className="mt-1 shrink-0 text-rose-400">–</span>
                    <div>
                      <p className="font-semibold text-foreground">
                        {isKo ? insight.title : (insight.titleEn ?? insight.title)}
                      </p>
                      <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                        {isKo ? insight.detail : (insight.detailEn ?? insight.detail)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ── Section 2: Creator Guide ── */}
          {creatorGuidePrompts.length > 0 && (
            <section className="p-5">
              <h3 className="mb-1.5 text-sm font-semibold text-foreground">Creator Guide</h3>
              <p className="mb-4 text-xs text-muted-foreground">
                {isKo
                  ? '각 프롬프트로 Discovery에서 적합한 크리에이터를 찾아보세요.'
                  : 'Use each prompt to find the right creators in Discovery.'}
              </p>
              <div className="flex flex-col gap-5">
                {creatorGuidePrompts.map((item, idx) => {
                  const label = isKo ? item.label : (item.labelEn ?? item.label);
                  const promptText = isKo ? item.prompt : (item.promptEn ?? item.prompt);
                  const isCopied = copiedIndex === idx;
                  return (
                    <div key={idx} className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-foreground">{label}</p>
                        <Button
                          variant={isCopied ? 'default' : 'ghost'}
                          size="sm"
                          className="h-6 shrink-0 gap-1 px-2 text-xs"
                          onClick={() => handleCopyPrompt(idx, promptText)}
                        >
                          <Copy className="size-3" />
                          {isCopied ? (isKo ? '복사됨!' : 'Copied!') : (isKo ? '복사' : 'Copy')}
                        </Button>
                      </div>
                      <div className="rounded-lg border bg-muted/40 px-4 py-3">
                        <p className="text-sm leading-relaxed text-foreground">{promptText}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-2"
                        onClick={() => handleFindCreators(promptText)}
                      >
                        <Search className="size-4" />
                        {isKo ? 'Find creators' : 'Find creators'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
